import { type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmDialogProps {
  open: boolean;
  onClose(): void;
  onConfirm(): void;
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
  icon?: ReactNode;
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  icon,
  loading = false,
}: ConfirmDialogProps) {
  if (!open) return null;

  // Portaled to <body> with z-[60] so it always sits above a `Dialog`
  // (portaled, z-50) when a form asks for confirmation before submitting.
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="confirm-dialog">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative z-10 mx-4 w-full max-w-md rounded-xl bg-card p-6 shadow-xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-1 text-muted-foreground hover:bg-muted transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex flex-col items-center gap-4 text-center">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-full ${
              variant === 'destructive' ? 'bg-destructive/10' : 'bg-primary/10'
            }`}
          >
            {icon || (
              <AlertTriangle
                className={`h-6 w-6 ${
                  variant === 'destructive' ? 'text-destructive' : 'text-primary'
                }`}
              />
            )}
          </div>

          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
          </div>

          <div className="flex w-full gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={loading}
              data-testid="confirm-dialog-cancel"
              className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              data-testid="confirm-dialog-confirm"
              className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                variant === 'destructive'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
            >
              {loading ? 'Processing...' : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
